import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Award, Globe2 } from "lucide-react";

const values = [
  { icon: Target, title: "Accuracy First", desc: "Every entry double-checked. Every reconciliation verified. 99.8% accuracy across all client books." },
  { icon: Users, title: "Dedicated Teams", desc: "You work with the same bookkeeper every day — someone who knows your business inside out." },
  { icon: Award, title: "Certified Expertise", desc: "QuickBooks ProAdvisors and Xero-certified professionals trained in US GAAP and Canadian standards." },
  { icon: Globe2, title: "US & Canada Focus", desc: "Deep familiarity with IRS and CRA requirements, state sales tax, GST/HST, and cross-border needs." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A00]">About Us</span>
          <h1 className="font-heading font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-4xl">
            The back office your business <span className="text-[#FF5A00]">deserves</span> — at a price it can afford.
          </h1>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="px-4 sm:px-6 lg:px-8 py-16 lg:py-20 lg:pr-16">
            <h2 className="font-heading font-extrabold text-3xl tracking-tight">Our story</h2>
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Core Sure Business Services Private Limited was founded on a simple observation: small and mid-sized businesses across the USA and Canada were paying too much for bookkeeping — or worse, doing it themselves at midnight after a full day of running their company.
              </p>
              <p>
                We built a different model. By combining experienced accounting professionals with efficient remote delivery, we provide the same quality as an in-house hire at a fraction of the cost — starting at just $9.99 per hour.
              </p>
              <p>
                Today, we serve hundreds of businesses across every US state and Canadian province: e-commerce stores, professional services firms, contractors, medical practices, restaurants, and more. Every client starts with a 1-week free trial, because we believe you should see our quality before spending a dollar.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6 border-t border-border pt-8">
              {[["500+", "Clients"], ["12+", "Industries"], ["6 yrs", "In business"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-mono-price text-2xl font-bold text-[#FF5A00]">{v}</p>
                  <p className="text-sm text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:border-l border-border">
            <img
              src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=1000&q=80"
              alt="Core Sure professional team meeting"
              className="w-full h-full object-cover min-h-[320px]"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-heading font-extrabold text-3xl tracking-tight mb-12">What we stand for</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-border p-8 hover-lift">
                <Icon size={28} className="text-[#FF5A00]" />
                <h3 className="font-heading font-bold text-lg mt-4">{title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight">Ready to meet your new bookkeeping team?</h2>
          <Link
            to="/appointment"
            data-testid="about-cta-btn"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-8 py-4 transition-colors whitespace-nowrap"
          >
            Book Appointment <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
